import { FC } from "react";
import { useSuspenseQuery } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";
import { Check, X } from "lucide-react";

import { Section, SectionHeader } from "@roqua/components/sections";
import { Button } from "@roqua/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@roqua/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@roqua/components/ui/table";

import { PageTitle } from "@/components/PageTitle";
import { LoginToken } from "@/components/respondents/LoginToken";
import { BackButton } from "@/components/ui/BackButton";
import { ActivateAccountButton } from "@/features/Dossier/ActivateAccountButton";
import { DeactivateAccountButton } from "@/features/Dossier/DeactivateAccountButton";
import { ManageTokensQuery } from "@/features/Dossier/ManageTokensQuery";
import { ReplaceTokenButton } from "@/features/Dossier/ReplaceTokenButton";
import { SendAccountInvitationButton } from "@/features/Dossier/SendAccountInvitation";
import { RespondentsForDossier } from "@/features/RespondentEditor/queries";
import { useRespondentById } from "@/features/Respondents/useRespondentById";
import { RespondentAccountStatus, RespondentTypeEnum } from "@/generated/gql/graphql";
import { useTranslation } from "@/i18n/client";
import { cn } from "@/lib/utils";
import { useSessionContext } from "@/session";

export const Route = createFileRoute(
  "/$organizationKey/$dossierId/dossier/respondents/$respondentId/manage-login",
)({
  component: RouteComponent,
});
function RouteComponent() {
  const { t } = useTranslation(["RespondentEditor", "common"]);
  const { organizationKey, dossierId, respondentId } = Route.useParams();
  const { user } = useSessionContext();
  const { data } = useSuspenseQuery(RespondentsForDossier, {
    variables: { organizationKey, dossierId },
  });
  const { data: tokenData } = useSuspenseQuery(ManageTokensQuery, {
    variables: { dossierId, respondentId },
  });

  const baseRespondent = data.currentDossier.respondents.find(
    (respondent) => respondent.id === respondentId,
  );

  if (!baseRespondent) {
    return <span>404</span>;
  }

  const respondent = tokenData.dossier.respondent;

  if (!respondent) {
    return <span>404</span>;
  }

  const accountStatus = respondent.accountStatus;

  const passwordLoginAllowed =
    user.rights.inviteRespondentsToCreateAccounts && respondent.type === RespondentTypeEnum.Patient;

  return (
    <>
      <PageTitle title="manageLogin" />
      <Section variant="header">
        <SectionHeader
          header={t(`type`, { context: baseRespondent.type })}
          buttons={<BackButton href="/$organizationKey/$dossierId/dossier" />}
        />
      </Section>

      <Section variant="content">
        <SectionHeader header={t("manageLogin.header")} />

        <div>
          {passwordLoginAllowed && <p>{t("manageLogin.description")}</p>}
          {passwordLoginAllowed && (
            <div>
              <Table className="w-auto">
                <TableHeader>
                  <TableRow>
                    <TableHead />
                    <TableHead>{t("loginToken")}</TableHead>
                    <TableHead>{t("password")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Invullen van vragenlijsten</TableCell>
                    <TableCell>
                      <Check />
                    </TableCell>
                    <TableCell>
                      <Check />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Inzien van eerder ingevulde vragenlijsten</TableCell>
                    <TableCell>
                      <X />
                    </TableCell>
                    <TableCell>
                      <Check />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Inzien van rapporten</TableCell>
                    <TableCell>
                      <X />
                    </TableCell>
                    <TableCell>
                      <Check />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <div
          className={cn(
            "grid grid-cols-1 gap-8 md:grid-cols-2",
            !passwordLoginAllowed && "md:grid-cols-1",
          )}
        >
          <TokenSection token={respondent.loginToken} respondentId={respondentId} />
          {passwordLoginAllowed && (
            <PasswordSection respondentId={respondentId} accountStatus={accountStatus} />
          )}
        </div>
      </Section>
    </>
  );
}

const TokenSection: FC<{
  token: string | null | undefined;
  respondentId: string;
}> = ({ token, respondentId }) => {
  const { t } = useTranslation(["RespondentEditor", "common"]);

  return (
    <div className="shadow-xs bg-background flex flex-col gap-4 border-primary p-4">
      <header className="text-lg">{t("manageLogin.loginTokenHeader")}</header>
      <div className="flex items-center gap-4">
        <p>
          {t("manageLogin.loginTokenIs")} <LoginToken token={token ?? ""} />
        </p>
      </div>
      <p>{t("manageLogin.loginTokenDescription")}</p>
      <div className="flex gap-x-4">
        <Dialog>
          <DialogTrigger asChild>
            <Button>{t("manageLogin.revokeToken")}</Button>
          </DialogTrigger>
          <DialogContent closeLabel={t("common:close")}>
            <DialogHeader>
              <DialogTitle className="pb-4">{t("common:confirm")}</DialogTitle>
              <DialogDescription className="pb-4">
                {t("manageLogin.revokeTokenDescription")}
              </DialogDescription>
              <ReplaceTokenButton respondentId={respondentId} />
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const PasswordSection: FC<{
  respondentId: string;
  accountStatus: RespondentAccountStatus;
}> = ({ respondentId, accountStatus }) => {
  const { t } = useTranslation(["RespondentEditor", "common"]);
  const respondent = useRespondentById(respondentId);

  const readyForActivation =
    accountStatus.passwordSet && accountStatus.mfaSet && !accountStatus.activated;

  return (
    <div className="bg-background shadow-xs flex flex-col justify-between gap-4 border-primary p-4">
      <header className="text-lg">{t("manageLogin.accountHeader")}</header>

      <AccountStatus status={accountStatus} />

      {!respondent?.email && (
        <div className="text-red-500">
          <p>{t("manageLogin.noEmail")}</p>
        </div>
      )}

      <div>
        {!readyForActivation && !accountStatus.activated && (
          <SendAccountInvitationButton
            respondentId={respondentId}
            sentBefore={!!accountStatus.setupEmailSent}
            disabled={accountStatus.passwordSet || !respondent?.email}
          />
        )}
        {readyForActivation && (
          <ActivateAccountButton
            respondentId={respondentId}
            buttonText={t("verifyLogin.verifyButtonText")}
          />
        )}
        {accountStatus.activated && (
          <DeactivateAccountButton
            respondentId={respondentId}
            buttonText={t("verifyLogin.verifyButtonTextRevert")}
          />
        )}
      </div>
    </div>
  );
};

const AccountStatus: FC<{ status: RespondentAccountStatus | undefined }> = ({ status }) => {
  const { t } = useTranslation("RespondentEditor");

  if (!status) {
    return null;
  }

  return (
    <div>
      {!status.activated && (
        <>
          <div className={cn("flex items-center gap-2", status.setupEmailSent && "text-green-800")}>
            <div>{status.setupEmailSent ? <Check /> : <X />}</div>
            <div>{t("manageLogin.accountStatus.setupEmailSent")}</div>
          </div>
          <div className={cn("flex items-center gap-2", status.passwordSet && "text-green-800")}>
            <div>{status.passwordSet ? <Check /> : <X />}</div>
            <div>{t("manageLogin.accountStatus.passwordSet")}</div>
          </div>
          <div className={cn("flex items-center gap-2", status.mfaSet && "text-green-800")}>
            <div>{status.mfaSet ? <Check /> : <X />}</div>
            <div>{t("manageLogin.accountStatus.mfaSet")}</div>
          </div>
        </>
      )}
      <div className={cn("flex items-center gap-2", status.activated && "text-green-800")}>
        <div>{status.activated ? <Check /> : <X />}</div>
        <div>{t("manageLogin.accountStatus.activated")}</div>
      </div>
      {status.activated && (
        <div
          className={cn(
            "flex items-center gap-2",
            status.passwordResetRequested && "text-green-800",
          )}
        >
          <div>{status.passwordResetRequested ? <Check /> : <X />}</div>
          <div>{t("manageLogin.accountStatus.passwordResetRequested")}</div>
        </div>
      )}
    </div>
  );
};
